import React from 'react';
import { Shield, Calendar } from 'lucide-react';

/**
 * Privacy Policy page for the Central Health Provider Portal.
 * Static content — linked from the footer and registration flow.
 */
const PrivacyPolicyPage = () => {
  const lastUpdated = 'April 1, 2026';

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Shield size={22} color="#0D9488" strokeWidth={2} />
            <h1 style={{ margin: 0 }}>Privacy Policy</h1>
          </div>
                  <p>How Central Health Plans collects, uses, and protects your information</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          color: '#6B7280', fontSize: 13, fontWeight: 500,
        }}>
          <Calendar size={14} strokeWidth={2} />
          <span>Last updated: {lastUpdated}</span>
        </div>
      </div>

      <div className="card" style={{ lineHeight: 1.65, color: '#374151' }}>
        <p>
          Central Health Plans, Inc. ("Central Health," "we," "us," or "our") is committed to
          protecting the privacy and security of the information you share with us through
          the Central Health Provider Portal (the "Portal"). This Privacy Policy explains how we
          collect, use, disclose, and safeguard your information.
        </p>

        <h2 style={sectionHeading}>1. Information We Collect</h2>
        <p>We collect the following categories of information:</p>
        <ul style={bulletList}>
          <li>
            <strong>Account information:</strong> name, professional title, email address,
            phone number, NPI, Tax ID, organization, and mailing address.
          </li>
          <li>
            <strong>Authentication data:</strong> username, password hash, multi-factor
            authentication settings, and login history.
          </li>
          <li>
            <strong>Usage data:</strong> pages visited, search queries, features used, IP
            address, device type, browser type, and session timestamps.
          </li>
          <li>
            <strong>Transactional data:</strong> eligibility inquiries, authorization
            requests, claims searches, and EOP downloads performed through the Portal.
          </li>
        </ul>

        <h2 style={sectionHeading}>2. How We Use Your Information</h2>
        <p>We use your information to:</p>
        <ul style={bulletList}>
          <li>Authenticate you and provide access to the Portal</li>
          <li>Process eligibility inquiries, prior authorizations, and claims</li>
          <li>Send operational notifications about your account and activity</li>
          <li>Detect, prevent, and investigate fraud or security incidents</li>
          <li>Comply with legal, regulatory, and contractual obligations</li>
          <li>Improve the security, performance, and functionality of the Portal</li>
        </ul>

        <h2 style={sectionHeading}>3. Protected Health Information (PHI)</h2>
        <p>
          PHI accessed through the Portal is governed by the Health Insurance Portability
          and Accountability Act of 1996 ("HIPAA") and our Business Associate Agreement
          with your organization. We use and disclose PHI only as permitted by HIPAA and
          your provider agreement.
        </p>

        <h2 style={sectionHeading}>4. Sharing of Information</h2>
        <p>We do not sell your personal information. We may share information with:</p>
        <ul style={bulletList}>
          <li>
            <strong>Service providers</strong> that perform services on our behalf (e.g.,
            hosting, email delivery, security monitoring) under written confidentiality
            and, where applicable, Business Associate Agreements.
          </li>
          <li>
            <strong>Regulators and law enforcement</strong> when required by law,
            subpoena, or valid legal process.
          </li>
          <li>
            <strong>Your organization's administrators</strong> for account management,
            audit, and oversight.
          </li>
          <li>
            <strong>Successors</strong> in the event of a merger, acquisition, or sale of
            assets, subject to equivalent privacy protections.
          </li>
        </ul>

        <h2 style={sectionHeading}>5. Security</h2>
        <p>
          We implement administrative, technical, and physical safeguards designed to
          protect your information, including encryption in transit, multi-factor
          authentication, access controls, activity logging, and routine security
          assessments. No system, however, is completely secure, and we cannot guarantee
          absolute security.
        </p>

        <h2 style={sectionHeading}>6. Cookies and Tracking</h2>
        <p>
          The Portal uses cookies and similar technologies for essential functionality
          (session management, security) and to improve performance. The Portal does not
          use third-party advertising cookies.
        </p>

        <h2 style={sectionHeading}>7. Data Retention</h2>
        <p>
          We retain account and activity data for as long as your account is active and
          thereafter as required to comply with legal, regulatory, and contractual
          obligations. Audit logs are retained for a minimum of six (6) years as required
          by HIPAA.
        </p>

        <h2 style={sectionHeading}>8. Your Rights</h2>
        <p>
          You may request access to, correction of, or deletion of your account
          information by contacting us. Certain requests may be subject to verification
          and legal retention requirements. Nothing in this Policy limits rights you may
          have as an individual whose PHI is processed through the Portal.
        </p>

        <h2 style={sectionHeading}>9. Children's Privacy</h2>
        <p>
          The Portal is intended for use by authorized healthcare providers and their
          staff. It is not directed to children under 13, and we do not knowingly collect
          information from children.
        </p>

        <h2 style={sectionHeading}>10. International Users</h2>
        <p>
          The Portal is hosted in the United States. By using the Portal, you consent to
          the processing of your information in the United States.
        </p>

        <h2 style={sectionHeading}>11. Changes to This Policy</h2>
        <p>
          We may update this Policy from time to time. Material changes will be
          communicated through the Portal or by email. The "Last updated" date above
          indicates when this Policy was most recently revised.
        </p>

        <h2 style={sectionHeading}>12. Contact</h2>
        <p>
          Questions or requests about this Privacy Policy should be directed to our
          Privacy Officer at
                  {' '}<a href="mailto:chproviderservices@centralhealth.net" style={linkStyle}>chproviderservices@centralhealth.net</a>{' '}
                  or by mail at
                  P. O. Box 17337,
                  Austin, TX 78760
        </p>
      </div>
    </div>
  );
};

const sectionHeading = {
  fontSize: 17,
  fontWeight: 700,
  color: '#1A1D21',
  marginTop: 24,
  marginBottom: 10,
};

const bulletList = {
  margin: '8px 0 16px 20px',
  paddingLeft: 4,
};

const linkStyle = {
  color: '#0D9488',
  fontWeight: 600,
  textDecoration: 'none',
};

export default PrivacyPolicyPage;
